import React, { useState } from 'react';
import {
  DollarSign,
  Calendar,
  Users,
  FileText,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { formatCurrency, formatDate } from '../../../utils/helpers';

const GameExpensesReport = ({
  games,
  filteredExpenses,
  players,
  formatCurrency: formatCurrencyProp
}) => {
  const [expandedSections, setExpandedSections] = useState({});

  // Use passed formatCurrency or imported one
  const format = formatCurrencyProp || formatCurrency;

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // IMPORTANT: Only show ACTUAL EXPENSES, not Initial Donations
  const actualExpensesOnly = filteredExpenses.filter(e => !e.isRegistrationFee && !e.isInitialDonation);

  // Calculate summary stats including tournament fees per game
  const calculateSummaryStats = () => {
    let totalWithTournamentFees = actualExpensesOnly.filter(e => !e.isTournamentFee).reduce((sum, e) => sum + e.amount, 0);
    let gamesWithExpenses = new Set();

    // Add tournament fee portions for each game
    games.forEach(game => {
      const gameExpenses = actualExpensesOnly.filter(e => e.gameId === game.id && !e.isTournamentFee);
      if (game.tournamentId) {
        const tournamentFee = filteredExpenses.find(e =>
          e.isTournamentFee &&
          e.tournamentId === game.tournamentId &&
          e.settled
        );
        if (tournamentFee) {
          const tournamentGames = games.filter(g => g.tournamentId === game.tournamentId);
          if (tournamentGames.length > 0) {
            totalWithTournamentFees += tournamentFee.amount / tournamentGames.length;
            gamesWithExpenses.add(game.id);
          }
        }
      }
      if (gameExpenses.length > 0) {
        gamesWithExpenses.add(game.id);
      }
    });

    const totalGamesWithExpenses = gamesWithExpenses.size;
    const avgPerGame = totalGamesWithExpenses > 0 ? totalWithTournamentFees / totalGamesWithExpenses : 0;

    return { totalWithTournamentFees, totalGamesWithExpenses, avgPerGame };
  };

  const { totalWithTournamentFees, totalGamesWithExpenses, avgPerGame } = calculateSummaryStats();

  // Filter games that have expenses or tournament fees
  const gamesWithData = games.filter(game => {
    const gameExpenses = actualExpensesOnly.filter(e => e.gameId === game.id && !e.isTournamentFee);
    const hasTournamentFee = game.tournamentId && filteredExpenses.some(e =>
      e.isTournamentFee &&
      e.tournamentId === game.tournamentId &&
      e.settled
    );
    return gameExpenses.length > 0 || hasTournamentFee;
  }).sort((a, b) => new Date(b.date) - new Date(a.date));

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-700 mb-1">Total Games</p>
          <p className="text-2xl font-bold text-blue-900">
            {totalGamesWithExpenses}
          </p>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-sm text-green-700 mb-1">Total Expenses</p>
          <p className="text-2xl font-bold text-green-900">
            {format(totalWithTournamentFees)}
          </p>
        </div>
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <p className="text-sm text-purple-700 mb-1">Avg per Game</p>
          <p className="text-2xl font-bold text-purple-900">
            {format(avgPerGame)}
          </p>
        </div>
      </div>

      {/* Calculation Documentation */}
      <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-5">
        <div
          className="flex items-center justify-between cursor-pointer"
          onClick={() => toggleSection('game-calc-docs')}
        >
          <div className="flex items-center gap-2">
            <FileText className="text-blue-600" size={20} />
            <h4 className="text-sm font-bold text-blue-900">How Per-Player Costs Are Calculated</h4>
          </div>
          {expandedSections['game-calc-docs'] ? <ChevronUp className="text-blue-600" size={20} /> : <ChevronDown className="text-blue-600" size={20} />}
        </div>

        {expandedSections['game-calc-docs'] && (
          <div className="mt-4 bg-white rounded-lg p-4 space-y-4">
            <div>
              <h5 className="text-xs font-bold text-gray-700 uppercase mb-2">Formula</h5>
              <div className="bg-gray-50 rounded p-3 font-mono text-xs space-y-1">
                <p className="text-gray-700">totalGameCost = Σ(approved expenses for game) + tournamentFeePerGame</p>
                <p className="text-gray-700">totalShares = regularPlayers + (superSubs × 0.5)</p>
                <p className="text-gray-700">perPlayerCost = totalGameCost ÷ totalShares</p>
                <p className="text-orange-700 font-semibold">superSubCost = perPlayerCost × 0.5</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-blue-50 rounded p-3">
                <h5 className="text-xs font-bold text-blue-900 mb-2">Example: Regular Squad</h5>
                <div className="text-xs text-gray-700 space-y-1">
                  <p>Total Cost: $330.00</p>
                  <p>Squad: 11 players (all regular)</p>
                  <p className="font-mono">Shares = 11 + (0 × 0.5) = 11</p>
                  <p className="font-semibold text-blue-700">Each pays: $330 ÷ 11 = $30.00</p>
                </div>
              </div>

              <div className="bg-yellow-50 rounded p-3">
                <h5 className="text-xs font-bold text-yellow-900 mb-2">Example: With Super Sub</h5>
                <div className="text-xs text-gray-700 space-y-1">
                  <p>Total Cost: $330.00</p>
                  <p>Squad: 11 players (10 reg + 1 SS)</p>
                  <p className="font-mono">Shares = 10 + (1 × 0.5) = 10.5</p>
                  <p className="font-semibold text-blue-700">Regular: $330 ÷ 10.5 = $31.43</p>
                  <p className="font-semibold text-orange-700">Super Sub: $31.43 × 0.5 = $15.71</p>
                </div>
              </div>
            </div>

            <div className="bg-gray-100 rounded p-3">
              <h5 className="text-xs font-bold text-gray-700 uppercase mb-2">Important Notes</h5>
              <ul className="text-xs text-gray-600 space-y-1 list-disc list-inside">
                <li>Only <span className="font-semibold">approved expenses</span> are included</li>
                <li>Super subs always pay <span className="font-semibold">50% of regular cost</span></li>
                <li>Tournament fees are split evenly across all tournament games</li>
                <li>All amounts rounded to 2 decimal places</li>
              </ul>
            </div>
          </div>
        )}
      </div>

      {/* Games with expenses */}
      {gamesWithData.map(game => {
        const gameExpenses = actualExpensesOnly.filter(e => e.gameId === game.id && !e.isTournamentFee);
        const regularExpensesCost = gameExpenses.reduce((sum, e) => sum + e.amount, 0);

        // Calculate tournament fee per game if applicable
        let tournamentFeePerGame = 0;
        let tournamentFee = null;
        if (game.tournamentId) {
          tournamentFee = filteredExpenses.find(e =>
            e.isTournamentFee &&
            e.tournamentId === game.tournamentId &&
            e.settled
          );
          if (tournamentFee) {
            const tournamentGames = games.filter(g => g.tournamentId === game.tournamentId);
            if (tournamentGames.length > 0) {
              tournamentFeePerGame = tournamentFee.amount / tournamentGames.length;
            }
          }
        }

        const totalGameCost = regularExpensesCost + tournamentFeePerGame;
        const squadSize = game.playing11?.length || 0;
        const superSubCount = game.superSubs?.length || 0;
        const regularPlayerCount = squadSize - superSubCount;

        // Calculate per player cost (accounting for super subs)
        let perPlayerCost = 0;
        if (squadSize > 0) {
          const totalShares = regularPlayerCount + (superSubCount * 0.5);
          perPlayerCost = totalShares > 0 ? totalGameCost / totalShares : 0;
        }

        return (
          <div key={game.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            {/* Game Header */}
            <div className="bg-gradient-to-r from-green-50 to-blue-50 border-b border-gray-200 p-6">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {game.name}
                  </h3>
                  <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {formatDate(game.date, {
                        weekday: 'short',
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </div>
                    {game.tournamentName && (
                      <div className="flex items-center gap-1">
                        <span className="font-semibold">🏆 {game.tournamentName}</span>
                      </div>
                    )}
                    {game.opponent && (
                      <div>vs {game.opponent}</div>
                    )}
                    {game.venue && (
                      <div>📍 {game.venue}</div>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500 mb-1">Total Cost</p>
                  <p className="text-3xl font-bold text-green-600">
                    {format(totalGameCost)}
                  </p>
                </div>
              </div>
            </div>

            {/* Squad Info */}
            <div className="bg-gray-50 border-b border-gray-200 px-6 py-3">
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-gray-500" />
                    <span className="font-semibold text-gray-700">
                      Squad: {squadSize} player{squadSize !== 1 ? 's' : ''}
                    </span>
                  </div>
                  {superSubCount > 0 && (
                    <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded">
                      {superSubCount} Super Sub{superSubCount !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                {squadSize > 0 && (
                  <div>
                    <span className="text-gray-500">Per Player: </span>
                    <span className="font-bold text-gray-900">
                      {format(perPlayerCost)}
                    </span>
                    {superSubCount > 0 && (
                      <span className="text-xs text-gray-500 ml-2">
                        (Super subs: {format(perPlayerCost * 0.5)})
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Expenses Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Submitted By</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Split Among</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {gameExpenses.map(expense => {
                    const submitterName = expense.createdByName ||
                      expense.addedByPlayerName ||
                      players.find(p => p.id === expense.addedByPlayerId)?.name ||
                      expense.createdBy || 'Unknown';

                    return (
                      <tr key={expense.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">
                          {expense.type || 'Other'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">
                          {expense.description || '-'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {submitterName}
                        </td>
                        <td className="px-6 py-4 text-sm font-semibold text-gray-900 text-right">
                          {format(expense.amount)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600 text-center">
                          {expense.splitAmong?.length || 0} players
                        </td>
                      </tr>
                    );
                  })}
                  {/* Tournament Fee Row (if applicable) */}
                  {tournamentFeePerGame > 0 && (
                    <tr className="bg-purple-50 hover:bg-purple-100">
                      <td className="px-6 py-4 text-sm font-medium text-purple-900">
                        🏆 Tournament Fee
                      </td>
                      <td className="px-6 py-4 text-sm text-purple-700">
                        {tournamentFee?.description || game.tournamentName || 'Tournament Entry'} (per game share)
                      </td>
                      <td className="px-6 py-4 text-sm text-purple-600">
                        Club Admin
                      </td>
                      <td className="px-6 py-4 text-sm font-semibold text-purple-900 text-right">
                        {format(tournamentFeePerGame)}
                      </td>
                      <td className="px-6 py-4 text-sm text-purple-600 text-center">
                        {squadSize} players
                      </td>
                    </tr>
                  )}
                  {/* Total Row */}
                  <tr className="bg-green-50 font-semibold">
                    <td colSpan="3" className="px-6 py-4 text-sm text-gray-900 text-right">
                      Total Game Cost:
                    </td>
                    <td className="px-6 py-4 text-sm text-green-700 text-right">
                      {format(totalGameCost)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 text-center">
                      {squadSize} players
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Squad Members (if selected) */}
            {squadSize > 0 && (
              <div className="bg-gray-50 border-t border-gray-200 px-6 py-4">
                <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Squad Members:</p>
                <div className="flex flex-wrap gap-2">
                  {game.playing11.map(playerId => {
                    const player = players.find(p => p.id === playerId);
                    const isSuperSub = game.superSubs?.includes(playerId);
                    const playerShare = isSuperSub ? perPlayerCost * 0.5 : perPlayerCost;

                    return (
                      <div
                        key={playerId}
                        className={`text-xs px-3 py-1 rounded-full ${isSuperSub
                          ? 'bg-yellow-100 text-yellow-700 border border-yellow-300'
                          : 'bg-blue-100 text-blue-700 border border-blue-300'
                          }`}
                      >
                        {player?.name || 'Unknown'} - {format(playerShare)}
                        {isSuperSub && ' (SS)'}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* No expenses message */}
      {gamesWithData.length === 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <DollarSign className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Expenses Found</h3>
          <p className="text-gray-600">
            No approved expenses for the selected date range.
          </p>
        </div>
      )}
    </div>
  );
};

export default GameExpensesReport;